import { Button } from "@/components/ui/button";
import {
  Star, ArrowRight, CheckCircle2, MapPin, Zap, Target, Users,
  Sparkles, Mail, Phone, Quote, Shield, Award, Clock,
} from "lucide-react";
import EditableText from "./EditableText";

export interface WebsiteCustomizations {
  primaryColor?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  ctaText?: string;
  ctaSecondary?: string;
  serviceTitle?: string;
  testimonial?: string;
  footerDesc?: string;
  productName?: string;
  phone?: string;
  email?: string;
  ctaFinalTitle?: string;
  ctaFinalDesc?: string;
  statSatisfaction?: string;
  statProjects?: string;
  hiddenSections?: string[];
}

interface PremiumTemplateProps {
  name: string;
  desc: string;
  product: string;
  client: string;
  city: string;
  sector: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const PremiumTemplate = ({ name, desc, product, client, city, sector, customizations, editMode = false, onCustomize }: PremiumTemplateProps) => {
  const c = customizations || {};
  const displayName = c.heroTitle || name;
  const displayDesc = c.heroSubtitle || desc;
  const displayCta = c.ctaText || "Prendre rendez-vous";
  const displayCtaSecondary = c.ctaSecondary || "Découvrir nos services";
  const displayTestimonial = c.testimonial || "Un accompagnement remarquable qui a transformé notre vision en réalité. Les résultats ont dépassé toutes nos attentes.";
  const displayFooterDesc = c.footerDesc || desc;
  const displayServiceTitle = c.serviceTitle || `Des solutions pensées pour ${client || "vous"}`;
  const displayProduct = c.productName || product || "Notre expertise";
  const displayPhone = c.phone || "01 23 45 67 89";
  const displayEmail = c.email || `contact@${name.toLowerCase().replace(/\s/g, "")}.fr`;
  const displayCtaFinalTitle = c.ctaFinalTitle || "Prêt à transformer votre activité ?";
  const displayCtaFinalDesc = c.ctaFinalDesc || `Contactez ${name} dès maintenant et découvrez comment nous pouvons accélérer votre croissance.`;
  const displayStatSatisfaction = c.statSatisfaction || "98%";
  const displayStatProjects = c.statProjects || "150+";
  const customStyle = c.primaryColor ? { "--primary": c.primaryColor } as React.CSSProperties : undefined;
  const edit = (field: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(field, v);
  return (
    <div className="bg-background" style={customStyle}>
      {/* ═══════ HERO ═══════ */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Layered backgrounds */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-card" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-30%,hsl(var(--primary)/0.18),transparent)]" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_80%_100%_at_100%_20%,hsl(var(--primary)/0.08),transparent)]" />
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: "60px 60px"
        }} />
        {/* Floating orbs */}
        <div className="absolute top-20 right-[15%] w-72 h-72 rounded-full bg-primary/5 blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-[10%] w-48 h-48 rounded-full bg-accent/8 blur-3xl animate-pulse [animation-delay:2s]" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              <Star className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary tracking-wide uppercase">{sector}</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground leading-[1.05]">
              {editMode ? (
                <EditableText
                  as="span"
                  value={displayName}
                  onChange={edit("heroTitle")}
                  editMode={editMode}
                  className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent"
                  data-field="hero-title"
                />
              ) : (
                displayName.split(" ").map((word, i) => (
                  <span key={i}>
                    {i === 0 ? <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">{word}</span> : ` ${word}`}
                  </span>
                ))
              )}
            </h1>

            <EditableText
              as="p"
              value={displayDesc}
              onChange={edit("heroSubtitle")}
              editMode={editMode}
              multiline
              className="text-lg md:text-xl text-muted-foreground max-w-lg leading-relaxed"
              data-field="hero-subtitle"
            />

            <div className="flex flex-wrap gap-4 pt-2">
              <Button variant="hero" size="lg" className="text-base h-14 px-8 rounded-xl shadow-lg shadow-primary/20">
                <EditableText as="span" value={displayCta} onChange={edit("ctaText")} editMode={editMode} data-field="cta-primary" />
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button variant="hero-outline" size="lg" className="text-base h-14 px-8 rounded-xl">
                <EditableText as="span" value={displayCtaSecondary} onChange={edit("ctaSecondary")} editMode={editMode} data-field="cta-secondary" />
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary" />
                {city}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Disponible
              </div>
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 border-2 border-background flex items-center justify-center">
                    <span className="text-[9px] font-bold text-primary">{["A", "M", "S", "L"][i]}</span>
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
                  <span className="text-[8px] font-bold text-primary">+99</span>
                </div>
              </div>
            </div>
          </div>

          {/* Hero visual card */}
          <div className="relative hidden lg:block">
            <div className="relative z-10">
              {/* Main feature card */}
              <div className="rounded-3xl bg-card/80 backdrop-blur-xl border border-border/50 p-10 shadow-2xl shadow-primary/5">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/20">
                      <Zap className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <div>
                      <EditableText as="h3" value={displayProduct} onChange={edit("productName")} editMode={editMode} className="text-xl font-bold text-foreground" data-field="product-name" />
                      <p className="text-sm text-muted-foreground">Solution personnalisée</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    Une approche sur mesure, pensée pour répondre aux défis spécifiques de votre activité et accélérer votre croissance.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: "Satisfaction", value: displayStatSatisfaction, field: "statSatisfaction" as const },
                      { label: "Projets livrés", value: displayStatProjects, field: "statProjects" as const },
                    ].map((stat) => (
                      <div key={stat.label} className="rounded-xl bg-background/50 border border-border/30 p-4 text-center">
                        <EditableText as="p" value={stat.value} onChange={edit(stat.field)} editMode={editMode} className="text-2xl font-bold text-primary" />
                        <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Floating small card */}
              <div className="absolute -bottom-6 -left-6 rounded-2xl bg-card border border-border/50 p-4 shadow-xl backdrop-blur-xl">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Projet terminé</p>
                    <p className="text-xs text-muted-foreground">il y a 2 jours</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Background glow */}
            <div className="absolute -inset-8 bg-gradient-to-br from-primary/10 via-transparent to-accent/5 rounded-3xl blur-2xl -z-10" />
          </div>
        </div>
      </section>

      {/* ═══════ SERVICES — BENTO GRID ═══════ */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-card/50 to-background" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Nos services</span>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              {editMode ? (
                <EditableText
                  as="span"
                  value={displayServiceTitle}
                  onChange={edit("serviceTitle")}
                  editMode={editMode}
                  className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent"
                  data-field="service-title"
                />
              ) : displayServiceTitle.includes("pour") ? (
                <>{displayServiceTitle.split("pour")[0]}pour{" "}
                <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{displayServiceTitle.split("pour")[1] || client || "vous"}</span></>
              ) : (
                <span>{displayServiceTitle}</span>
              )}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Chaque service est conçu avec précision pour maximiser l'impact sur votre activité.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Large featured card */}
            <div className="md:col-span-2 md:row-span-2 group rounded-3xl border border-border/50 bg-card/60 backdrop-blur p-10 hover:border-primary/30 transition-all duration-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors duration-500" />
              <div className="relative space-y-6">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/10">
                  <Target className="h-7 w-7 text-primary-foreground" />
                </div>
                <EditableText as="h3" value={displayProduct} onChange={edit("productName")} editMode={editMode} className="text-2xl font-bold text-foreground" />
                <p className="text-muted-foreground text-lg leading-relaxed max-w-lg">
                  Notre offre phare, conçue avec une expertise de pointe pour répondre précisément à vos attentes les plus exigeantes. Un accompagnement complet, de la stratégie à l'exécution.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  {["Stratégie", "Exécution", "Suivi", "Résultats"].map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-primary font-medium pt-2 group-hover:gap-3 transition-all">
                  <span>En savoir plus</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>

            {/* Smaller cards */}
            {[
              { icon: Users, title: "Accompagnement", desc: "Un suivi personnalisé pour garantir votre réussite à chaque étape du parcours." },
              { icon: Sparkles, title: "Innovation", desc: "Des méthodes modernes et des outils de pointe pour des résultats exceptionnels." },
            ].map((s) => (
              <div key={s.title} className="group rounded-3xl border border-border/50 bg-card/60 backdrop-blur p-8 hover:border-primary/30 transition-all duration-500">
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                    <s.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TRUST / STATS ═══════ */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-background to-primary/5" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "150+", label: "Clients satisfaits", icon: Users },
              { value: "98%", label: "Taux de satisfaction", icon: Award },
              { value: "5 ans", label: "D'expérience", icon: Clock },
              { value: city, label: "Notre localisation", icon: MapPin },
            ].map((s) => (
              <div key={s.label} className="text-center space-y-3 group">
                <div className="h-12 w-12 mx-auto rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-primary">{s.value}</p>
                <p className="text-sm text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ WHY US ═══════ */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Pourquoi nous choisir</span>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
                L'excellence au service de votre{" "}
                <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">réussite</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Nous ne sommes pas simplement un prestataire. Nous sommes votre partenaire stratégique, engagé dans votre succès à long terme.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Shield, title: "Fiabilité", desc: "Des engagements tenus, des résultats mesurables" },
                { icon: Target, title: "Précision", desc: "Chaque action est calibrée pour un impact maximum" },
                { icon: Zap, title: "Réactivité", desc: "Un temps de réponse imbattable sur toutes vos demandes" },
                { icon: Award, title: "Excellence", desc: "Des standards de qualité parmi les plus élevés du marché" },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border border-border/50 bg-card/50 p-6 space-y-3 hover:border-primary/20 transition-colors">
                  <item.icon className="h-6 w-6 text-primary" />
                  <h4 className="font-bold text-foreground">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ TESTIMONIAL ═══════ */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <Quote className="h-12 w-12 text-primary/30 mx-auto" />
          <blockquote className="text-2xl md:text-3xl font-medium text-foreground leading-relaxed italic">
            {editMode ? (
              <EditableText as="span" value={displayTestimonial} onChange={edit("testimonial")} editMode={editMode} multiline data-field="testimonial" />
            ) : (
              `"${displayTestimonial}"`
            )}
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center">
              <span className="text-sm font-bold text-primary">C.D</span>
            </div>
            <div className="text-left">
              <p className="font-semibold text-foreground">Client dirigeant</p>
              <p className="text-sm text-muted-foreground">{sector}</p>
            </div>
          </div>
          <div className="flex justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 text-primary fill-primary" />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-primary/5" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_50%,hsl(var(--primary)/0.08),transparent)]" />
        <div className="relative max-w-3xl mx-auto px-6 text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground">
            <EditableText as="span" value={displayCtaFinalTitle} onChange={edit("ctaFinalTitle")} editMode={editMode} data-field="cta-final-title" />
          </h2>
          <EditableText as="p" value={displayCtaFinalDesc} onChange={edit("ctaFinalDesc")} editMode={editMode} multiline className="text-lg text-muted-foreground max-w-xl mx-auto" data-field="cta-final-desc" />
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Button variant="hero" size="lg" className="h-14 px-8 text-base rounded-xl shadow-lg shadow-primary/20">
              <Mail className="h-5 w-5 mr-2" />
              Nous contacter
            </Button>
            <Button variant="hero-outline" size="lg" className="h-14 px-8 text-base rounded-xl">
              <Phone className="h-5 w-5 mr-2" />
              <EditableText as="span" value={displayPhone} onChange={edit("phone")} editMode={editMode} data-field="phone" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="border-t border-border/50 py-16 bg-card/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-lg">{name.charAt(0).toUpperCase()}</span>
                </div>
                <span className="text-xl font-bold text-foreground">{name}</span>
              </div>
              <EditableText as="p" value={displayFooterDesc} onChange={edit("footerDesc")} editMode={editMode} multiline className="text-sm text-muted-foreground max-w-sm leading-relaxed" data-field="footer-desc" />
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground text-sm uppercase tracking-wider">Services</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="hover:text-primary cursor-pointer transition-colors">{product || "Service principal"}</li>
                <li className="hover:text-primary cursor-pointer transition-colors">Accompagnement</li>
                <li className="hover:text-primary cursor-pointer transition-colors">Innovation</li>
                <li className="hover:text-primary cursor-pointer transition-colors">Conseil</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground text-sm uppercase tracking-wider">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> <EditableText as="span" value={displayEmail} onChange={edit("email")} editMode={editMode} data-field="email" /></li>
                <li className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> <EditableText as="span" value={displayPhone} onChange={edit("phone")} editMode={editMode} /></li>
                <li className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {city}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>© 2026 {name} — Tous droits réservés</p>
            <p>Propulsé par <span className="text-primary font-semibold">Grafton IA</span></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PremiumTemplate;
